/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toSortParams } from '@data/dto/api.dto';
import { toProfession } from '@data/dto/profession.dto';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import {
  Profession,
  ProfessionHttp,
  ProfessionWizard,
} from '@data/models/profession.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class ProfessionService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly professionEndpoint = '/profession-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listProfessions(
    filters: FilterParams,
  ): Promise<PaginateResult<Profession[]>> {
    const { data: response, error } = await this.API?.get<{
      data: ProfessionHttp[];
      meta: Meta;
    }>(this.professionEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toProfession) || [],
      totalSize: response?.meta.page.total || 0,
    };
  }

  public async upsertProfession({
    id,
    ...professionWizard
  }: ProfessionWizard): Promise<Profession | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<ProfessionWizard, 'id'> },
        { data: ProfessionHttp }
      >(this.professionEndpoint, { data: professionWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toProfession(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: ProfessionWizard },
      { data: ProfessionHttp }
    >(`${this.professionEndpoint}/${id}`, {
      data: { ...professionWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toProfession(data?.data || {});
  }

  public async deleteProfession(id: string): Promise<Profession | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: ProfessionHttp;
    }>(`${this.professionEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toProfession(data?.data || {});
  }

  public async sortProfession(
    sortParams: DatatableReorderParams<Profession>,
  ): Promise<Profession | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.professionEndpoint}/${id}/sort`,
      {
        data: toSortParams('profession_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
